import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HSGLogo from '@/components/feature/HSGLogo';

const navLinks = [
  { label: 'About Us', path: '/about' },
  { label: 'Philosophy', path: '/philosophy' },
  { label: 'Property Types', path: '/properties' },
  { label: 'Services', path: '/services' },
  { label: 'Our Team', path: '/team' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // On non-home pages, always show the solid navbar
  const solidNav = !isHome || scrolled;

  const handleNavClick = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solidNav ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-8 md:px-16 h-20 flex items-center justify-between">
        {/* Logo */}
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          className="flex items-center cursor-pointer"
        >
          <HSGLogo variant={solidNav ? 'dark' : 'light'} size="sm" />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.path); }}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                  solidNav
                    ? isActive
                      ? 'text-[#8B7355]'
                      : 'text-[#2C2C2C] hover:text-[#8B7355]'
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {link.label}
              </a>
            );
          })}
          <button
            onClick={() => navigate('/portal')}
            className={`flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-md border transition-all cursor-pointer whitespace-nowrap ${
              solidNav
                ? 'border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white'
                : 'border-white/50 text-white/90 hover:border-white hover:text-white'
            }`}
          >
            <i className="ri-shield-keyhole-line"></i>
            Client Portal
          </button>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
            solidNav ? 'text-[#2C2C2C]' : 'text-white'
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-md border-t border-[#E8E6E3] px-8 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <a
              key={link.path}
              href={link.path}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.path); }}
              className="text-sm font-medium text-[#2C2C2C] hover:text-[#8B7355] tracking-wide transition-colors cursor-pointer"
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </header>
  );
}
